import { SecurityClearance } from "@fairfleet/geotab";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isProtectedClearanceId } from "../clearanceHelpers";
import { ClearanceFilters } from "./ClearanceFilters";
import { ROOT_SECURITY_GROUP_ID } from "@/constants/geotabGroupIds";

interface ClearanceEditorProps {
  /** The selected clearance from the list, or `null` when creating a new one. */
  clearance: { id: string; name?: string; comments?: string } | null;
  /** The selected clearance's parent id (ignored when creating). */
  parentId: string;
  parentOptions: { id: string; name?: string }[];
  securityFilters: NonNullable<SecurityClearance["securityFilters"]>;
  successMessage: string | null;
  errorMessage: string | null;
  isSaving: boolean;
  isRemoving: boolean;
  onSave: (values: { name: string; comment: string; parentId: string }) => void;
  onRemove: () => void;
}

/** Holds the form state; mount with a `key` per clearance so it resets on selection. */
export function ClearanceEditor({
  clearance,
  parentId,
  parentOptions,
  securityFilters,
  successMessage,
  errorMessage,
  isSaving,
  isRemoving,
  onSave,
  onRemove,
}: ClearanceEditorProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(clearance?.name ?? "");
  const [comment, setComment] = useState(clearance?.comments ?? "");
  const [newParentId, setNewParentId] = useState(ROOT_SECURITY_GROUP_ID);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const isProtected = clearance ? isProtectedClearanceId(clearance.id) : false;

  return (
    <Paper
      variant="outlined"
      sx={{ width: { xs: "100%", lg: "70%" }, minWidth: 0, boxSizing: "border-box", p: 2, overflow: "auto" }}
    >
      <Stack spacing={1.25}>
        <Typography variant="h6" sx={{ overflowWrap: "anywhere" }}>
          {clearance ? (clearance.name ?? clearance.id) : t("clearances.new")}
        </Typography>

        {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        {isProtected ? <Alert severity="info">{t("clearances.protectedHint")}</Alert> : null}

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
          {clearance ? (
            <TextField
              label={t("clearances.parent")}
              value={parentOptions.find(group => group.id === parentId)?.name ?? parentId}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
          ) : (
            <TextField
              label={t("clearances.parent")}
              value={newParentId}
              onChange={event => setNewParentId(event.target.value)}
              fullWidth
              size="small"
              select
            >
              {parentOptions.map(group => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name ?? group.id}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            label={t("clearances.name")}
            value={name}
            onChange={event => setName(event.target.value)}
            fullWidth
            size="small"
            disabled={isProtected}
          />
        </Stack>

        <TextField
          label={t("clearances.comment")}
          value={comment}
          onChange={event => setComment(event.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={4}
          disabled={isProtected}
        />

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            onClick={() => onSave({ name, comment, parentId: newParentId })}
            disabled={isSaving || isProtected}
          >
            {t("clearances.save")}
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            variant="outlined"
            onClick={() => setIsDeleteConfirmOpen(true)}
            disabled={!clearance || isRemoving || isProtected}
          >
            {t("clearances.delete")}
          </Button>
        </Stack>

        {clearance ? (
          <ClearanceFilters filters={securityFilters} />
        ) : null}
      </Stack>

      <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
        <DialogTitle>{t("clearances.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("clearances.deleteConfirmBody", { name: clearance?.name ?? clearance?.id ?? "" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)}>{t("clearances.cancel")}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setIsDeleteConfirmOpen(false);
              onRemove();
            }}
          >
            {t("clearances.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
